import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatDate, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Loader2, Tag, Users, TrendingUp, Percent, DollarSign, History, Settings, Package } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => `R${(parseFloat(v) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

export default function PriceListsPage() {
  const { id } = useParams();
  if (id === "new") return <CreatePriceList />;
  if (id) return <PriceListDetail plId={id} />;
  return <PriceListList />;
}

function PriceListList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/price-lists?search=${search}&limit=50`)
      .then(r => { setItems(r.data.price_lists); setTotal(r.data.total); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, [search]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="price-lists-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-[#1F2937]">Price Lists</h1>
          <p className="text-sm text-gray-500 mt-1">{total} price list{total !== 1 ? "s" : ""}</p>
        </div>
        <Button data-testid="create-price-list-btn" onClick={() => navigate("/price-lists/new")} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
          <Plus className="w-4 h-4 mr-1.5" />New Price List
        </Button>
      </div>
      <div className="mb-4">
        <Input data-testid="pl-search-input" placeholder="Search price lists..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs rounded-sm" />
      </div>
      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-sm" /> : items.length === 0 ? (
        <Card className="border rounded-sm"><CardContent className="p-12 text-center text-gray-400"><Tag className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No price lists found</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(pl => (
            <Card key={pl.pl_id} data-testid={`pl-card-${pl.pl_id}`} className="border rounded-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/price-lists/${pl.pl_id}`)}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-[#1F2937]">{pl.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{pl.description || "No description"}</p>
                  </div>
                  <div className="flex gap-1.5">
                    <Badge className={`rounded-full text-xs ${pl.status === "Active" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-100 text-gray-500"} border`}>{pl.status}</Badge>
                    {pl.is_default && <Badge className="rounded-full text-xs bg-blue-50 text-blue-700 border-blue-200 border">Default</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500 mt-3 pt-3 border-t">
                  <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{pl.type}</span>
                  <span className="flex items-center gap-1"><Package className="w-3 h-3" />{(pl.items || []).length} items</span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{(pl.customer_ids || []).length} customers</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CreatePriceList() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", description: "", type: "Retail", currency: "ZAR", is_default: false });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const res = await api.post("/price-lists", form);
      toast.success("Price list created");
      navigate(`/price-lists/${res.data.pl_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="create-price-list">
      <Button variant="ghost" size="sm" onClick={() => navigate("/price-lists")} className="mb-4"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
      <h1 className="font-heading font-bold text-2xl text-[#1F2937] mb-6">New Price List</h1>
      <Card className="border rounded-sm"><CardContent className="p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Name</label>
            <Input data-testid="pl-name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-sm" placeholder="e.g. Wholesale Prices" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Type</label>
            <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
              <SelectTrigger data-testid="pl-type-select" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Retail">Retail</SelectItem>
                <SelectItem value="Wholesale">Wholesale</SelectItem>
                <SelectItem value="Custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Description</label>
          <textarea data-testid="pl-description-input" className="w-full rounded-sm border p-2.5 text-sm" rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="pl-default" checked={form.is_default} onChange={(e) => setForm({...form, is_default: e.target.checked})} className="rounded" />
          <label htmlFor="pl-default" className="text-sm text-gray-700">Set as default price list</label>
        </div>
        <div className="flex gap-2 pt-2">
          <Button data-testid="save-price-list-btn" onClick={save} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Create Price List
          </Button>
          <Button variant="outline" onClick={() => navigate("/price-lists")} className="rounded-sm">Cancel</Button>
        </div>
      </CardContent></Card>
    </div>
  );
}

function PriceListDetail({ plId }) {
  const navigate = useNavigate();
  const [pl, setPl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [addItemDialog, setAddItemDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [itemForm, setItemForm] = useState({ list_price: 0, volume_discounts: [] });
  const [bulkForm, setBulkForm] = useState({ update_type: "percentage", update_value: 0, category: "" });
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [actioning, setActioning] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", description: "", type: "", status: "", is_default: false });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/price-lists/${plId}`)
      .then(r => {
        setPl(r.data);
        setSelectedCustomers(r.data.customer_ids || []);
        setEditForm({ name: r.data.name, description: r.data.description, type: r.data.type, status: r.data.status, is_default: r.data.is_default });
      })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, [plId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/products?limit=500").then(r => setProducts(r.data.products || []));
    api.get("/customers?limit=500").then(r => setCustomers(r.data.customers || []));
    api.get("/categories").then(r => setCategories(r.data.categories || []));
  }, []);

  const addItem = async () => {
    if (!selectedProduct) return toast.error("Select a product");
    setActioning(true);
    try {
      await api.post(`/price-lists/${plId}/items`, { items: [{ product_id: selectedProduct, list_price: itemForm.list_price, volume_discounts: itemForm.volume_discounts }] });
      toast.success("Item added"); setAddItemDialog(false); setSelectedProduct(""); setItemForm({ list_price: 0, volume_discounts: [] }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActioning(false); }
  };

  const removeItem = async (productId) => {
    try {
      await api.delete(`/price-lists/${plId}/items/${productId}`);
      toast.success("Item removed"); load();
    } catch (e) { toast.error("Failed to remove"); }
  };

  const assignCustomers = async () => {
    setActioning(true);
    try {
      await api.post(`/price-lists/${plId}/assign-customers`, { customer_ids: selectedCustomers });
      toast.success("Customers updated"); setAssignDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActioning(false); }
  };

  const bulkUpdate = async () => {
    setActioning(true);
    try {
      const res = await api.post(`/price-lists/${plId}/bulk-update`, bulkForm);
      toast.success(res.data.message); setBulkDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActioning(false); }
  };

  const viewHistory = (productId) => {
    api.get(`/products/${productId}/price-history?limit=50`).then(r => {
      setPriceHistory(r.data.history || []);
      setHistoryDialog(true);
    }).catch(() => toast.error("Failed to load history"));
  };

  const saveEdit = async () => {
    setActioning(true);
    try {
      await api.put(`/price-lists/${plId}`, editForm);
      toast.success("Price list updated"); setEditDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActioning(false); }
  };

  const deletePl = async () => {
    if (!window.confirm("Delete this price list?")) return;
    try {
      await api.delete(`/price-lists/${plId}`);
      toast.success("Deleted"); navigate("/price-lists");
    } catch (e) { toast.error("Failed to delete"); }
  };

  const addVolumeDiscount = () => setItemForm({ ...itemForm, volume_discounts: [...itemForm.volume_discounts, { min_qty: 0, price: 0 }] });
  const removeVolumeDiscount = (idx) => setItemForm({ ...itemForm, volume_discounts: itemForm.volume_discounts.filter((_, i) => i !== idx) });

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!pl) return <div className="p-8 text-center text-gray-400">Not found</div>;

  const plItems = pl.items || [];

  return (
    <div data-testid="price-list-detail">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/price-lists")}><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="font-heading font-bold text-2xl text-[#1F2937]">{pl.name}</h1>
          <p className="text-sm text-gray-500">{pl.description || pl.type} | {plItems.length} items | {(pl.customer_ids || []).length} customers</p>
        </div>
        <div className="flex gap-1.5">
          <Badge className={`rounded-full text-sm px-3 py-1 border ${pl.status === "Active" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-100 text-gray-500"}`}>{pl.status}</Badge>
          {pl.is_default && <Badge className="rounded-full text-sm px-3 py-1 border bg-blue-50 text-blue-700 border-blue-200">Default</Badge>}
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        <Button data-testid="add-item-btn" size="sm" className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm" onClick={() => setAddItemDialog(true)}><Plus className="w-4 h-4 mr-1" />Add Product</Button>
        <Button data-testid="assign-customers-btn" size="sm" variant="outline" className="rounded-sm" onClick={() => setAssignDialog(true)}><Users className="w-4 h-4 mr-1" />Assign Customers</Button>
        <Button data-testid="bulk-update-btn" size="sm" variant="outline" className="rounded-sm" onClick={() => setBulkDialog(true)}><Percent className="w-4 h-4 mr-1" />Bulk Update</Button>
        <Button data-testid="edit-pl-btn" size="sm" variant="outline" className="rounded-sm" onClick={() => setEditDialog(true)}><Settings className="w-4 h-4 mr-1" />Edit</Button>
        <Button data-testid="delete-pl-btn" size="sm" variant="outline" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50" onClick={deletePl}><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
      </div>

      {/* Assigned Customers */}
      {(pl.customer_ids || []).length > 0 && (
        <Card className="border rounded-sm mb-4"><CardContent className="p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1"><Users className="w-3.5 h-3.5" />Assigned Customers</h3>
          <div className="flex flex-wrap gap-1.5">
            {pl.customer_ids.map(cId => {
              const c = customers.find(cc => cc.customer_id === cId);
              return <Badge key={cId} className="rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">{c?.company_name || cId}</Badge>;
            })}
          </div>
        </CardContent></Card>
      )}

      {/* Price List Items */}
      <Card className="border rounded-sm mb-6"><div className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Products & Prices ({plItems.length})</h3>
        {plItems.length === 0 ? (
          <div className="text-center text-gray-400 py-8"><Tag className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">No products added yet</p></div>
        ) : (
          <table data-testid="pl-items-table" className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="p-2.5 text-left text-xs font-semibold uppercase text-gray-500">SKU</th>
              <th className="p-2.5 text-left text-xs font-semibold uppercase text-gray-500">Product</th>
              <th className="p-2.5 text-right text-xs font-semibold uppercase text-gray-500">Base Price</th>
              <th className="p-2.5 text-right text-xs font-semibold uppercase text-gray-500">List Price</th>
              <th className="p-2.5 text-left text-xs font-semibold uppercase text-gray-500">Volume Discounts</th>
              <th className="p-2.5 text-right text-xs font-semibold uppercase text-gray-500">Actions</th>
            </tr></thead>
            <tbody>{plItems.map((item, i) => (
              <tr key={i} className="border-b border-gray-100">
                <td className="p-2.5 font-mono text-xs">{item.sku || "-"}</td>
                <td className="p-2.5 font-medium">{item.product_name}</td>
                <td className="p-2.5 text-right text-gray-500">{fmt(item.base_price)}</td>
                <td className="p-2.5 text-right font-semibold text-[#E63946]">{fmt(item.list_price)}</td>
                <td className="p-2.5">{(item.volume_discounts || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1">{item.volume_discounts.map((vd, vi) => (
                    <Badge key={vi} className="rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200">{vd.min_qty}+: {fmt(vd.price)}</Badge>
                  ))}</div>
                ) : <span className="text-gray-400 text-xs">None</span>}</td>
                <td className="p-2.5 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => viewHistory(item.product_id)}><History className="w-3.5 h-3.5 text-gray-400" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => removeItem(item.product_id)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div></Card>

      {/* Add Item Dialog */}
      <Dialog open={addItemDialog} onOpenChange={setAddItemDialog}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Add Product to Price List</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Product</label>
              <Select value={selectedProduct} onValueChange={(v) => { setSelectedProduct(v); const p = products.find(pp => pp.product_id === v); if (p) setItemForm({...itemForm, list_price: p.selling_price || 0}); }}>
                <SelectTrigger data-testid="add-product-select"><SelectValue placeholder="Select product..." /></SelectTrigger>
                <SelectContent>{products.filter(p => p.product_id && !plItems.some(pi => pi.product_id === p.product_id)).map(p => (
                  <SelectItem key={p.product_id} value={p.product_id}>{p.sku} - {p.product_name} ({fmt(p.selling_price)})</SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">List Price</label>
              <Input data-testid="item-list-price" type="number" step="0.01" value={itemForm.list_price} onChange={(e) => setItemForm({...itemForm, list_price: parseFloat(e.target.value) || 0})} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-500 uppercase">Volume Discounts</label>
                <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={addVolumeDiscount}><Plus className="w-3 h-3 mr-1" />Add</Button>
              </div>
              {itemForm.volume_discounts.map((vd, i) => (
                <div key={i} className="flex gap-2 items-center mb-2">
                  <Input type="number" placeholder="Min Qty" className="w-24 h-8 text-xs" value={vd.min_qty} onChange={(e) => { const u = [...itemForm.volume_discounts]; u[i].min_qty = parseInt(e.target.value) || 0; setItemForm({...itemForm, volume_discounts: u}); }} />
                  <span className="text-xs text-gray-500">units @</span>
                  <Input type="number" step="0.01" placeholder="Price" className="w-28 h-8 text-xs" value={vd.price} onChange={(e) => { const u = [...itemForm.volume_discounts]; u[i].price = parseFloat(e.target.value) || 0; setItemForm({...itemForm, volume_discounts: u}); }} />
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => removeVolumeDiscount(i)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddItemDialog(false)}>Cancel</Button>
            <Button data-testid="confirm-add-item-btn" onClick={addItem} disabled={actioning || !selectedProduct} className="bg-[#E63946] hover:bg-[#C1121F] text-white">
              {actioning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Customers Dialog */}
      <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Assign Customers</DialogTitle></DialogHeader>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {customers.map(c => (
              <label key={c.customer_id} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={selectedCustomers.includes(c.customer_id)} onChange={(e) => {
                  if (e.target.checked) setSelectedCustomers([...selectedCustomers, c.customer_id]);
                  else setSelectedCustomers(selectedCustomers.filter(id => id !== c.customer_id));
                }} className="rounded" />
                <span className="text-sm">{c.company_name}</span>
                <span className="text-xs text-gray-400 ml-auto">{c.email}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog(false)}>Cancel</Button>
            <Button data-testid="confirm-assign-customers-btn" onClick={assignCustomers} disabled={actioning} className="bg-[#E63946] hover:bg-[#C1121F] text-white">
              {actioning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Save ({selectedCustomers.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
        <DialogContent><DialogHeader><DialogTitle>Bulk Price Update</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Update Type</label>
              <Select value={bulkForm.update_type} onValueChange={(v) => setBulkForm({...bulkForm, update_type: v})}>
                <SelectTrigger data-testid="bulk-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed Amount (R)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Value ({bulkForm.update_type === "percentage" ? "%" : "R"})</label>
              <Input data-testid="bulk-value-input" type="number" step="0.01" value={bulkForm.update_value} onChange={(e) => setBulkForm({...bulkForm, update_value: parseFloat(e.target.value) || 0})} placeholder="e.g. 10 for +10%, -5 for -5%" />
              <p className="text-xs text-gray-400 mt-1">Use positive for increase, negative for decrease</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Category Filter (optional)</label>
              <Select value={bulkForm.category || "all"} onValueChange={(v) => setBulkForm({...bulkForm, category: v === "all" ? "" : v})}>
                <SelectTrigger data-testid="bulk-category-select"><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.filter(c => c.category_id && c.name).map(c => <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialog(false)}>Cancel</Button>
            <Button data-testid="confirm-bulk-update-btn" onClick={bulkUpdate} disabled={actioning || bulkForm.update_value === 0} className="bg-[#E63946] hover:bg-[#C1121F] text-white">
              {actioning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-1" />}Update Prices
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price History Dialog */}
      <Dialog open={historyDialog} onOpenChange={setHistoryDialog}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Price History</DialogTitle></DialogHeader>
          {priceHistory.length === 0 ? (
            <div className="text-center text-gray-400 py-6"><History className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">No price history</p></div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b">
                  <th className="p-2 text-left text-xs font-semibold text-gray-500">Date</th>
                  <th className="p-2 text-right text-xs font-semibold text-gray-500">Old</th>
                  <th className="p-2 text-right text-xs font-semibold text-gray-500">New</th>
                  <th className="p-2 text-left text-xs font-semibold text-gray-500">Type</th>
                  <th className="p-2 text-left text-xs font-semibold text-gray-500">By</th>
                </tr></thead>
                <tbody>{priceHistory.map((h, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="p-2 text-xs">{formatDate(h.changed_at)}</td>
                    <td className="p-2 text-right text-xs text-gray-500">{fmt(h.old_price)}</td>
                    <td className="p-2 text-right text-xs font-medium">{fmt(h.new_price)}</td>
                    <td className="p-2"><Badge className="rounded-full text-xs bg-gray-100 text-gray-600">{h.change_type}</Badge></td>
                    <td className="p-2 text-xs">{h.changed_by}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Price List Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent><DialogHeader><DialogTitle>Edit Price List</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Name</label>
              <Input data-testid="edit-pl-name" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} /></div>
            <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Description</label>
              <textarea className="w-full rounded-sm border p-2.5 text-sm" rows={2} value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Type</label>
                <Select value={editForm.type} onValueChange={(v) => setEditForm({...editForm, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Retail">Retail</SelectItem><SelectItem value="Wholesale">Wholesale</SelectItem><SelectItem value="Custom">Custom</SelectItem></SelectContent>
                </Select></div>
              <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Status</label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
                </Select></div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={editForm.is_default} onChange={(e) => setEditForm({...editForm, is_default: e.target.checked})} className="rounded" />
              <label className="text-sm text-gray-700">Set as default</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button data-testid="confirm-edit-pl-btn" onClick={saveEdit} disabled={actioning} className="bg-[#E63946] hover:bg-[#C1121F] text-white">
              {actioning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
